import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, Feather, Sunrise } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

export default function WhatIsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    if (user?.has_paid) navigate('/dashboard');
    else if (user) navigate('/betalning');
    else navigate('/registrera');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="whatis-page">
      <Navigation />
      <div className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">

          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium text-[#A0655A] mb-4">
              Om metoden
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium text-[#111111] leading-tight" data-testid="whatis-title">
              Vad är emotionell frihet?
            </h1>
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="space-y-5 text-[#111111]/65 leading-relaxed text-base mb-14">
            <p>
              Emotionell frihet handlar om att leva i kontakt med sig själv — utan att omedvetet styras av tidigare erfarenheter, invanda mönster eller obearbetade känslor.
            </p>
            <p>
              Det innebär inte att bli fri från känslor. Det innebär att kunna vara i dem utan att fastna, undvika eller reagera automatiskt. Det handlar om att förstå det som sker inom dig och därifrån kunna välja hur du vill leva, snarare än att fortsätta upprepa det som redan varit.
            </p>
            <p>
              Många människor bär på en inre påverkan som de inte alltid är fullt medvetna om. Tidigare upplevelser, relationer och sätt att hantera livet formar hur vi tänker, känner och agerar. Med tiden kan dessa mönster bli så integrerade att de upplevs som en naturlig del av ens personlighet — trots att de i själva verket är inlärda sätt att skydda sig, anpassa sig eller hantera det som en gång varit svårt.
            </p>
            <p>
              Det är ofta här en känsla av att ha tappat kontakten med sig själv uppstår. Man fungerar, tar ansvar och gör det som förväntas, men upplever samtidigt en inre oro, trötthet eller tomhet som inte riktigt går att förklara.
            </p>
            <p>
              Emotionell frihet innebär att börja bryta den omedvetna kopplingen mellan det som har varit och hur du lever idag.
            </p>
          </motion.div>

          {/* The three phases */}
          <motion.div {...fadeUp(0.15)} className="mb-14">
            <div className="h-px bg-[#EBE3DF] mb-14" />
            <h2 className="heading-font text-2xl sm:text-3xl font-medium text-[#111111] mb-4 text-center">
              Tre faser som speglar din inre utveckling
            </h2>
            <p className="text-[#111111]/55 text-center mb-10 max-w-lg mx-auto leading-relaxed">
              Jag har byggt metoden kring tre faser som jag om och om igen sett i de processer människor genomgår — både i mitt arbete och i mitt eget liv.
            </p>

            <div className="space-y-4">
              {[
                { icon: Eye, title: "Klarhet", desc: "Du blir medveten om dig själv, dina mönster och det som pågår inom dig. Det här steget handlar om att se — ärligt och utan att döma." },
                { icon: Feather, title: "Frigörelse", desc: "Du förstår vad du bär på och ger dig själv tillåtelse att inte längre bära det. Här börjar du släppa det som inte tjänar dig längre." },
                { icon: Sunrise, title: "Förankring", desc: "Du kan leva i linje med dig själv, mer närvarande och fritt. Det handlar om att landa i den du verkligen är." },
              ].map((phase, i) => (
                <motion.div key={i} {...fadeUp(0.05 * i)}
                  className="flex gap-4 sm:gap-5 bg-[#F9F3EF]/60 border border-[#EBE3DF]/50 rounded-xl p-5 sm:p-6"
                  data-testid={`whatis-phase-${i}`}>
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-10 h-10 rounded-full bg-[#A0655A]/10 text-[#A0655A] flex items-center justify-center">
                      <phase.icon size={18} strokeWidth={1.8} />
                    </div>
                  </div>
                  <div>
                    <h3 className="heading-font text-lg sm:text-xl font-medium text-[#111111] mb-1.5">{phase.title}</h3>
                    <p className="text-sm text-[#111111]/55 leading-relaxed">{phase.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Closing */}
          <motion.div {...fadeUp(0.2)} className="mb-12">
            <div className="h-px bg-[#EBE3DF] mb-14" />
            <div className="space-y-5 text-[#111111]/65 leading-relaxed text-base">
              <p>
                Emotionell frihet är inte en snabb lösning. Det är en process som sker över tid — ett arbete som kräver närvaro, vilja att förstå och mod att möta det som finns inom en.
              </p>
              <p>
                Jag har skapat den här metoden för att du ska kunna göra det arbetet på egen hand, men med en tydlig struktur och vägledning. Steg för steg, i din egen takt.
              </p>
            </div>
          </motion.div>

          {/* CTA */}
          {!user?.has_paid && (
            <motion.div {...fadeUp(0.25)} className="text-center pt-4">
              <Button onClick={handleCTA}
                className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-5 text-base"
                data-testid="whatis-cta-btn">
                {user ? 'Gå till betalning' : 'Börja din resa'}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
